import React, { useRef } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import HTMLReactParser from 'html-react-parser';
import { useLocation } from 'react-router-dom';
import toLower from 'lodash/toLower';
import format from 'date-fns/format';

// GLOBAL VARIABLES
import { DATE_FORMATS, NO_VIEW_FILE_ACTIVITIES } from 'globals.js';

// GLOBAL FUNCTIONS
import { formatDate, formatDateTime, isDate } from 'functions.js';

// PROTOTYPE
import Log from '../prototype/Log';

// LOCAL PARTS
import ActivityItem from './activityLog/ActivityItem';
import ActivityTable from './activityLog/ActivityTable';
import ActivityTools from './activityLog/ActivityTools';
import DownloadButton from './activityLog/DownloadButton';
import ReportLink from './activityLog/ReportLink';

// STYLES
import styles from './activityLog.module.scss';

// FUNCTIONS
const parseNotes = (value, activityType) => {
  let output = value || '';
  output = output.replaceAll(',', ', ');
  if (value.includes('viewreport')) {
    const shouldNotViewFile = NO_VIEW_FILE_ACTIVITIES.some(a => 
      toLower((activityType || '').replace(/[^a-zA-Z] /g, '')).includes(a)
    );
    const anchor = HTMLReactParser(value);
    output = <ReportLink {...anchor.props} shouldChunkDownload={false} shouldViewFile={!shouldNotViewFile} />;
  }
  else if (value.includes('href')){
    output = HTMLReactParser(value);
  }
  return output;
};

// MAIN COMPONENT
const ActivityLog = props => {

  // REACT ROUTER
  const { pathname } = useLocation();

  // REFS
  const dates = useRef([]);

  // RENDER
  return (
    <Log
      title="Recent Activity"
      tools={<ActivityTools {...props} />}
      item={({
        index,
        activity_log_user_email_address,
        activity_log_user_full_name,
        activity_type,
        request_date,
        requester_name,
        is_sales,
        date_created,
        notes,
        details,
        aryFields,
        tableData = details || aryFields
      }) => {

        let formattedDate = formatDate(date_created);
        dates.current[index] = formattedDate;

        let showDate;

        if (formattedDate !== dates.current[index - 1]) {
          if (formattedDate === formatDate(new Date)) formattedDate = 'Today';
          showDate = true;
        }
        tableData = tableData?.filter(d => d.new_value || d.original_value)?.map(d => ({ ...d, is_sales }));

        return (<>
          {showDate && formattedDate &&
            <h4 className={clsx('text-primary fw-bold', styles.date)}>{formattedDate}</h4>
          }
          <ActivityItem
            title={<>
              <a className="fw-bolder text-body text-decoration-none" href={`mailto:${activity_log_user_email_address}`}>{activity_log_user_full_name}</a>
              &nbsp;
              {activity_type}
              {!!requester_name && <>&nbsp;from Upload Request by {requester_name} on {formatDateTime(request_date)}</>}
            </>}
            timeStamp={isDate(date_created) && format(new Date(date_created), DATE_FORMATS.time)}
          >
            {!!notes &&
              <div className={styles.notes}>{parseNotes(notes, activity_type)}</div>
            }
            {!!tableData.length &&
              <ActivityTable
                columns={[
                  {
                    key: pathname.includes('inventory') ? 'field_label' : 'field', // This is necessary because of an inconsistency between sales and inventory endpoints
                    format: HTMLReactParser,
                    label: 'Field'
                  },
                  {
                    key: 'original_value',
                    format: HTMLReactParser,
                    as: DownloadButton
                  },
                  {
                    key: 'new_value',
                    classNameMethod: ({ display_color_code }) => display_color_code ? `text-${display_color_code}` : '',
                    format: HTMLReactParser,
                    as: DownloadButton
                  }
                ]}
                data={tableData}
              />
            }
          </ActivityItem>
        </>)
      }}
      {...props}
    />
  );
};

// EXPORT
ActivityLog.Tab = props => <Log.Tab pill={false} {...props} />;
export default ActivityLog;
